// Operator-facing quality feedback page. Shows results from released hidden
// golden sets only — never live calibration progress, never other operators'
// scores. Accessible from the operator dashboard once the operator has at
// least one released set.

import clsx from 'clsx';
import { ArrowLeft, ChevronRight, RefreshCw, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type GoldenSelfReview,
  type GoldenSelfSummary,
  getMyGoldenReview,
  getMyGoldenSummary,
} from '../golden';
import { useStore } from '../store';
import { showToast } from '../toast';

function fmtDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString();
}

function decodeBase64Url(encoded: string): Uint8Array {
  const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function bitAt(buf: Uint8Array, i: number): boolean {
  const bi = Math.floor(i / 8);
  const off = i % 8;
  const byte = buf[bi] ?? 0;
  return ((byte >> off) & 1) === 1;
}

interface MismatchRange {
  start: number;
  end: number; // exclusive
  operator_value: boolean;
  truth_value: boolean;
}

// Walk the bit arrays and collapse consecutive mismatched seconds into
// ranges. Each range carries the (operator, truth) pair so the UI can show
// "second 12-15: you said Yes, truth was No".
function diffMismatches(
  operatorEncoded: string,
  truthEncoded: string,
  durationSeconds: number
): MismatchRange[] {
  const op = decodeBase64Url(operatorEncoded);
  const truth = decodeBase64Url(truthEncoded);
  const total = durationSeconds + 1;
  const mismatches: MismatchRange[] = [];

  let i = 0;
  while (i < total) {
    const opBit = bitAt(op, i);
    const truthBit = bitAt(truth, i);
    if (opBit === truthBit) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < total && bitAt(op, j) === opBit && bitAt(truth, j) === truthBit) j += 1;
    mismatches.push({ start: i, end: j, operator_value: opBit, truth_value: truthBit });
    i = j;
  }
  return mismatches;
}

export function QualityFeedback() {
  const { operator, showOperatorDashboard } = useStore();
  const [summary, setSummary] = useState<GoldenSelfSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!operator) return;
    setLoading(true);
    try {
      const data = await getMyGoldenSummary(operator);
      setSummary(data);
    } catch (err) {
      console.error('Failed to load quality feedback', err);
      showToast('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [operator]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!operator) {
    return (
      <div className="mx-auto max-w-md py-32 text-center text-brand-text-secondary">
        Sign in first.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-b border-brand-border pb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
            <ShieldCheck className="h-8 w-8 text-emerald-400" />
            Quality feedback
          </h1>
          <p className="mt-2 text-brand-text-secondary text-sm">
            Released calibration results · {operator}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50 disabled:opacity-50"
          >
            <RefreshCw className={clsx('h-3.5 w-3.5', loading && 'animate-spin')} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => void showOperatorDashboard()}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-4 py-2 text-sm font-medium text-brand-text-secondary transition hover:border-brand-text-secondary/50 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        </div>
      </div>

      {selectedSetId ? (
        <ReviewSetView
          operator={operator}
          summary={summary}
          setId={selectedSetId}
          onBack={() => setSelectedSetId(null)}
        />
      ) : (
        <SummaryListView
          summary={summary}
          loading={loading}
          onOpen={(setId) => setSelectedSetId(setId)}
        />
      )}
    </div>
  );
}

function SummaryListView({
  summary,
  loading,
  onOpen,
}: {
  summary: GoldenSelfSummary | null;
  loading: boolean;
  onOpen: (setId: string) => void;
}) {
  if (loading) {
    return <div className="text-center py-12 text-brand-text-secondary">Loading…</div>;
  }
  if (!summary || summary.sets.length === 0) {
    return (
      <div className="rounded-xl border border-brand-border bg-brand-surface p-8 text-center text-sm text-brand-text-secondary">
        No released calibration results yet. Check back later.
      </div>
    );
  }

  const aggAcc =
    summary.aggregate.total_bits > 0
      ? (summary.aggregate.match_bits / summary.aggregate.total_bits) * 100
      : null;

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Released sets" value={summary.sets.length} />
        <Stat label="Completed attempts" value={summary.aggregate.attempts_completed} />
        <Stat label="Aggregate accuracy" value={aggAcc != null ? `${aggAcc.toFixed(1)}%` : '—'} />
      </div>

      <div className="rounded-xl border border-brand-border bg-brand-surface overflow-hidden">
        <div className="border-b border-brand-border px-5 py-3 text-sm font-semibold text-brand-text-primary">
          Per-set
        </div>
        <div className="divide-y divide-brand-border">
          {summary.sets.map((s) => {
            const acc = s.total_bits > 0 ? (s.match_bits / s.total_bits) * 100 : null;
            return (
              <button
                key={s.set_id}
                type="button"
                onClick={() => onOpen(s.set_id)}
                className="grid grid-cols-12 gap-2 px-5 py-3 text-sm w-full text-left hover:bg-brand-surface-highlight/30"
              >
                <div className="col-span-6">
                  <div className="font-medium text-brand-text-primary truncate flex items-center gap-2">
                    {s.name}
                    <ChevronRight className="h-4 w-4 text-brand-text-secondary" />
                  </div>
                  <div className="text-xs text-brand-text-secondary">
                    Released {fmtDate(s.feedback_released_at_ms)}
                  </div>
                </div>
                <div className="col-span-2 text-right text-brand-text-secondary">
                  {s.attempts_completed}/{s.attempts_total}
                </div>
                <div className="col-span-2 text-right text-brand-text-secondary text-xs">
                  {s.match_bits}/{s.total_bits} bits
                </div>
                <div className="col-span-2 text-right font-bold text-brand-text-primary">
                  {acc != null ? `${acc.toFixed(1)}%` : '—'}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function ReviewSetView({
  operator,
  summary,
  setId,
  onBack,
}: {
  operator: string;
  summary: GoldenSelfSummary | null;
  setId: string;
  onBack: () => void;
}) {
  const set = useMemo(
    () => summary?.sets.find((s) => s.set_id === setId) ?? null,
    [summary, setId]
  );
  const [reviews, setReviews] = useState<Record<string, GoldenSelfReview | 'pending' | 'failed'>>(
    {}
  );
  const [episodeIds, setEpisodeIds] = useState<string[] | null>(null);

  // The summary endpoint returns aggregate counts but not the per-episode list.
  // We resolve per-episode reviews lazily: each row in this view maps to an
  // episode the operator completed under this set. To enumerate them we'd
  // need a second endpoint; for now we rely on the operator visiting their
  // own /me/quality endpoints in series. Practical workaround: use the admin
  // results detail (operator can't call that), so we expose the per-episode
  // list through the review endpoint by trying each episode id from the
  // golden review one-by-one. Simpler: ask the user to paste/specify an
  // episode they want to review.
  //
  // For Phase 5, we ship a minimal flow: the user picks a specific episode
  // id and we fetch that one review. Future work can list all the operator's
  // completed episodes for this set.

  const [episodeQuery, setEpisodeQuery] = useState('');

  const handleLookup = async () => {
    const epId = episodeQuery.trim();
    if (!epId) return;
    setReviews((prev) => ({ ...prev, [epId]: 'pending' }));
    try {
      const r = await getMyGoldenReview(operator, setId, epId);
      if (!r) {
        setReviews((prev) => ({ ...prev, [epId]: 'failed' }));
        showToast(`No completed review for ${epId}`);
        return;
      }
      setReviews((prev) => ({ ...prev, [epId]: r }));
      setEpisodeIds((prev) => (prev?.includes(epId) ? prev : [...(prev ?? []), epId]));
      setEpisodeQuery('');
    } catch (err) {
      console.error('Lookup failed', err);
      setReviews((prev) => ({ ...prev, [epId]: 'failed' }));
    }
  };

  if (!set) {
    return (
      <div className="text-center py-12 text-brand-text-secondary">
        Set is not yet released or not part of your assignments.
      </div>
    );
  }

  const acc = set.total_bits > 0 ? (set.match_bits / set.total_bits) * 100 : null;

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-xs text-brand-text-secondary hover:text-white"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All sets
      </button>

      <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
        <h2 className="text-xl font-bold text-brand-text-primary">{set.name}</h2>
        <div className="text-sm text-brand-text-secondary mt-1">
          Released {fmtDate(set.feedback_released_at_ms)} · {set.attempts_completed} completed ·{' '}
          {acc != null ? `${acc.toFixed(1)}% accuracy` : 'no scored attempts'}
        </div>
      </div>

      <div className="rounded-xl border border-brand-border bg-brand-surface p-5 space-y-3">
        <h3 className="text-sm font-semibold text-brand-text-primary">Look up an episode</h3>
        <p className="text-xs text-brand-text-secondary">
          Type an episode_id from this set to see your answer vs the ground truth.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={episodeQuery}
            onChange={(e) => setEpisodeQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleLookup();
            }}
            placeholder="episode_id"
            className="flex-1 h-9 rounded-lg border border-brand-border bg-brand-dark px-3 text-xs font-mono text-brand-text-primary focus:border-brand-orange focus:outline-none"
          />
          <button
            type="button"
            onClick={() => void handleLookup()}
            className="rounded-lg bg-brand-orange px-4 py-2 text-xs font-semibold text-white hover:bg-brand-orange-hover"
          >
            Look up
          </button>
        </div>
      </div>

      {(episodeIds ?? []).map((epId) => (
        <ReviewCard key={epId} entry={reviews[epId] ?? null} />
      ))}
    </div>
  );
}

// Wrapper handles the three non-data states without touching any hooks. The
// completed-review render path lives in CompletedReviewCard so its useMemo
// runs in a stable hook order across re-renders.
function ReviewCard({ entry }: { entry: GoldenSelfReview | 'pending' | 'failed' | null }) {
  if (!entry) return null;
  if (entry === 'pending') {
    return <div className="text-xs text-brand-text-secondary">Loading…</div>;
  }
  if (entry === 'failed') {
    return <div className="text-xs text-red-400">Could not load review.</div>;
  }
  return <CompletedReviewCard entry={entry} />;
}

function CompletedReviewCard({ entry }: { entry: GoldenSelfReview }) {
  const acc = entry.total_bits > 0 ? (entry.match_bits / entry.total_bits) * 100 : null;
  const mismatches = useMemo(() => {
    try {
      return diffMismatches(entry.operator_encoded, entry.ground_truth_encoded, entry.duration);
    } catch {
      return [];
    }
  }, [entry]);

  return (
    <div className="rounded-xl border border-brand-border bg-brand-surface p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-sm text-brand-text-primary">{entry.episode_id}</div>
          <div className="text-xs text-brand-text-secondary mt-1">
            Completed {fmtDate(entry.completed_at_ms)} · {entry.duration + 1} bits
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold font-mono text-brand-text-primary">
            {acc != null ? `${acc.toFixed(1)}%` : '—'}
          </div>
          <div className="text-xs text-brand-text-secondary">
            {entry.match_bits}/{entry.total_bits} bits
          </div>
        </div>
      </div>

      <div className="border-t border-brand-border pt-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary mb-2">
          Mismatches ({mismatches.length})
        </h4>
        {mismatches.length === 0 ? (
          <div className="text-xs text-emerald-400">Perfect match — no mismatches.</div>
        ) : (
          <div className="space-y-1 max-h-72 overflow-auto text-xs">
            {mismatches.map((m, i) => (
              <div
                key={`${m.start}-${m.end}`}
                className="flex items-center gap-2 rounded border border-red-500/20 bg-red-500/5 px-3 py-1.5"
              >
                <span className="font-mono text-brand-text-secondary shrink-0">
                  {m.start === m.end - 1 ? `s${m.start}` : `s${m.start}-${m.end - 1}`}
                </span>
                <span className="text-red-400 shrink-0">
                  you: {m.operator_value ? 'qualified' : 'not'}
                </span>
                <span className="text-brand-text-secondary shrink-0">→</span>
                <span className="text-emerald-400 shrink-0">
                  truth: {m.truth_value ? 'qualified' : 'not'}
                </span>
                {i >= 9 && mismatches.length > 10 && i === 9 && (
                  <span className="text-brand-text-secondary text-[10px]">
                    (+{mismatches.length - 10} more)
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl border border-brand-border bg-brand-surface p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
        {label}
      </div>
      <div className="mt-1 text-xl font-bold font-mono text-brand-text-primary">{value}</div>
    </div>
  );
}
